import EmailComposerDialogShell from '@/components/email-composer/EmailComposerDialogShell';
import EmailComposerRecipientLabelRow from '@/components/email-composer/EmailComposerRecipientLabelRow';
import { useEmailComposer } from '@/hooks/use-email-composer';
import type { EmailComposerMode } from '@/hooks/use-email-composer-mode';
import { useEmailIntegrationStatus } from '@/hooks/use-email-integration-status';
import { Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import InvitationBulkOutreachRecap from './InvitationBulkOutreachRecap';

/** What the table hands over when the brand opens the composer. */
export type InvitationBulkEmailTarget = Omit<
  Extract<EmailComposerMode, { type: 'invitation-outreach-bulk' }>,
  'type'
>;

interface InvitationBulkEmailModalProps {
  /** The selection frozen when the bar was clicked; `null` keeps the dialog closed. */
  target: InvitationBulkEmailTarget | null;
  /** `sent` is true once the grouped send returned, so the caller can clear its selection. */
  onClose: (outcome: { sent: boolean }) => void;
}

const CLOSED_TARGET: InvitationBulkEmailTarget = {
  kind: 'outreach',
  selection: { ids: [] },
  recipientCount: 0,
  mayIncludeExpired: false,
};

/**
 * Grouped email from the brand's own mailbox to the invitations selected in the
 * table (or to the whole filter), as a follow-up ("Relancer") or a plain email
 * ("Envoyer un e-mail"). Same editor, variables and templates as the single
 * invitation email of the same kind; gates on a usable mailbox, and swaps to a
 * recap of queued and skipped invitations once the send returns.
 */
const InvitationBulkEmailModal: FC<InvitationBulkEmailModalProps> = ({ target, onClose }) => {
  const { t } = useTranslation();
  // Strict send capability: only a HEALTHY mailbox can send from the brand's
  // address, so a `needs_reconnect` token must not reach the composer.
  const { hasUsableMailbox } = useEmailIntegrationStatus();

  const { kind, selection, recipientCount, mayIncludeExpired } = target ?? CLOSED_TARGET;
  const mode = useMemo(
    () => ({
      type: 'invitation-outreach-bulk' as const,
      kind,
      selection,
      recipientCount,
      mayIncludeExpired,
    }),
    [kind, selection, recipientCount, mayIncludeExpired]
  );
  const isFollowUp = kind === 'follow_up';

  // Tracked via a ref so the composer's close callback reads the latest value:
  // the result slot is reset in the same close.
  const didSendRef = useRef(false);
  const composer = useEmailComposer({
    hasProgramContext: false,
    mode,
    onClose: () => {
      const sent = didSendRef.current;
      didSendRef.current = false;
      onClose({ sent });
    },
  });
  const result = composer.invitationOutreachBulkResult;

  useEffect(() => {
    if (result) {
      didSendRef.current = true;
    }
  }, [result]);

  const recipientSlot = (
    <EmailComposerRecipientLabelRow
      label={t('creatorOutreach.bulk.recipientLabel')}
      value={t('invitations.bulkEmail.recipients', { count: recipientCount })}
      caption={
        // Only a follow-up renews every expired invitation it reaches.
        isFollowUp && mayIncludeExpired ? (
          <Text fontSize="xs" color="fg.muted">
            {t('invitations.bulkEmail.renewalNote')}
          </Text>
        ) : undefined
      }
    />
  );

  return (
    <EmailComposerDialogShell
      composer={composer}
      isOpen={target !== null}
      title={
        isFollowUp ? t('invitations.bulkEmail.followUpTitle') : t('invitations.bulkEmail.title')
      }
      showComposer={hasUsableMailbox && !result}
      labels={{
        backToEdit: t('bulkEmail.compose.confirm.backToEdit'),
        send: t('invitations.bulkEmail.send', { count: recipientCount }),
        confirmTitle: t('bulkEmail.compose.confirm.title'),
        confirmDescription: t('invitations.bulkEmail.confirmDescription', {
          count: recipientCount,
          email: composer.senderEmail,
        }),
        confirmSend: t('invitations.bulkEmail.send', { count: recipientCount }),
      }}
      recipientSlot={recipientSlot}
      recapSlot={
        result ? (
          <InvitationBulkOutreachRecap result={result} onDone={composer.handleClose} />
        ) : undefined
      }
    />
  );
};

export default InvitationBulkEmailModal;
